/* plentum-motion.js — Plentum motion + interaction core v1 (WS-MOTION).
   Vanilla, no libraries. IntersectionObserver lazy init, prefers-reduced-motion respected, transform/opacity only, no layout shift.
   Patterns ported from omni.studio (reveal, step player, count-up, tabs, scrollspy) and the Plentum kit spec.
   Contract: EXECUTION/README.md section 2 (+ MOTION-KIT.md). Re-scan dynamic markup with PM.init(rootEl). */
(function () {
  'use strict';
  if (window.PM) return;
  var d = document, de = d.documentElement, IO = 'IntersectionObserver' in window, RM = mm('(prefers-reduced-motion: reduce)');
  function mm(q) { try { return matchMedia(q).matches; } catch (e) { return false; } }
  function $$(s, c) { return [].slice.call((c || d).querySelectorAll(s)); }
  function num(v, f) { v = parseFloat(v); return isNaN(v) ? f : v; }
  function has(e, a) { return e.hasAttribute(a); }
  function ev(el, n, det) { el.dispatchEvent(new CustomEvent(n, { detail: det, bubbles: true })); }
  function find(el, s) { return el.matches && el.matches(s) ? [el].concat($$(s, el)) : $$(s, el); }
  function mk(t, c, h) { var e = d.createElement(t); if (c) e.className = c; if (h) e.innerHTML = h; return e; }
  var BASE = (function () {
    var s = d.currentScript || $$('script[src*="plentum-motion"]')[0];
    try { return s.src.replace(/[^\/?#]*([?#].*)?$/, ''); } catch (e) { return ''; }
  })();

  /* ---------- shared visibility observer: reveal, stagger, count ---------- */
  var vio = IO && new IntersectionObserver(function (es) {
    es.forEach(function (e) { if (e.isIntersecting) { vio.unobserve(e.target); show(e.target); } });
  }, { rootMargin: '0px 0px -8% 0px' });
  function show(el) { el.classList.add('pm-in'); if (el._pmc) el._pmc(); }
  function watch(el) { if (RM || !vio) show(el); else vio.observe(el); }

  function reveal(el) {
    var dl = el.getAttribute('data-pm-delay'); if (dl) el.style.setProperty('--pm-delay', num(dl, 0) + 'ms');
    if (has(el, 'data-pm-stagger')) {
      var st = num(el.getAttribute('data-pm-stagger'), 0); if (st) el.style.setProperty('--pm-stagger', st + 'ms');
      [].forEach.call(el.children, function (c, i) { c.style.setProperty('--pm-i', Math.min(i, 10)); });
    }
    watch(el);
  }

  /* count-up: the markup already holds the final number (SEO, no-JS, reduced motion); it is replayed once when seen */
  function count(el) {
    var to = num(el.getAttribute('data-pm-count'), 0), dec = num(el.getAttribute('data-pm-decimals'), 0),
      pre = el.getAttribute('data-pm-prefix') || '', suf = el.getAttribute('data-pm-suffix') || '';
    function f(v) { return pre + v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }) + suf; }
    el.textContent = f(to);
    if (RM || !vio) { el.classList.add('pm-in'); return; }
    el.style.minWidth = el.offsetWidth + 'px';
    el._pmc = function () {
      var t0 = performance.now(), dur = num(el.getAttribute('data-pm-dur'), 1100);
      (function step(t) {
        var p = Math.min(1, (t - t0) / dur);
        el.textContent = f(to * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(step); else el.textContent = f(to);
      })(t0);
    };
    vio.observe(el);
  }

  /* ---------- tabs: [data-pm-tabs] > [data-pm-tab] + [data-pm-panel] (paired by value, else by index) ---------- */
  function tabs(box) {
    var tb = $$('[data-pm-tab]', box), pn = $$('[data-pm-panel]', box), n = tb.length, uid = 'pmt' + Math.random().toString(36).slice(2, 7), hash = has(box, 'data-pm-hash');
    if (!n) return;
    if (tb[0].parentNode && tb[0].parentNode !== box) tb[0].parentNode.setAttribute('role', 'tablist');
    function panelOf(t, i) {
      var k = t.getAttribute('data-pm-tab');
      return (k && pn.filter(function (p) { return p.getAttribute('data-pm-panel') === k; })[0]) || pn[i];
    }
    function pick(i, focus, user) {
      tb.forEach(function (t, j) {
        var on = i === j, p = panelOf(t, j);
        t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; t.classList.toggle('is-active', on);
        if (p) {
          if (on && p.hidden && user && !RM) { p.classList.remove('pm-swap'); void p.offsetWidth; p.classList.add('pm-swap'); }
          p.hidden = !on;
        }
        if (on && focus) t.focus();
      });
      if (hash && user) { try { history.replaceState(null, '', '#' + (tb[i].getAttribute('data-pm-tab') || i)); } catch (e) {} }
      ev(box, 'pm:tab', { index: i });
    }
    tb.forEach(function (t, i) {
      var p = panelOf(t, i);
      t.setAttribute('role', 'tab'); if (!t.id) t.id = uid + 't' + i;
      if (p) { if (!p.id) p.id = uid + 'p' + i; t.setAttribute('aria-controls', p.id); p.setAttribute('role', 'tabpanel'); p.setAttribute('aria-labelledby', t.id); }
      if (t.tagName !== 'BUTTON') t.setAttribute('tabindex', '0');
      t.addEventListener('click', function () { pick(i, 0, 1); });
      t.addEventListener('keydown', function (e) {
        var k = e.key, to = -1;
        if (k === 'ArrowRight' || k === 'ArrowDown') to = (i + 1) % n; else if (k === 'ArrowLeft' || k === 'ArrowUp') to = (i + n - 1) % n;
        else if (k === 'Home') to = 0; else if (k === 'End') to = n - 1;
        if (to > -1) { e.preventDefault(); pick(to, 1, 1); }
      });
    });
    var a = tb.map(function (t) { return t.getAttribute('aria-selected') === 'true' || has(t, 'data-pm-active'); }).indexOf(true), h = hash && location.hash.slice(1);
    if (h) { var hi = tb.map(function (t) { return t.getAttribute('data-pm-tab'); }).indexOf(h); if (hi > -1) a = hi; }
    pick(a < 0 ? 0 : a);
  }

  /* ---------- steps: auto-advancing player. Time is owned by CSS (the tick animation), pauses off-screen / hover / focus / touch ---------- */
  function steps(box) {
    var st = $$('[data-pm-step]', box), n = st.length, i = 0, user = false, vis = !IO, held = {}, tg = box.querySelector('[data-pm-toggle]');
    if (!n) return;
    var auto = box.getAttribute('data-pm-auto') !== 'false' && !RM, dwell = num(box.getAttribute('data-pm-dwell'), 4200);
    box.classList.add('pm-live');
    function paint() {
      box.setAttribute('data-active', i); box.style.setProperty('--pm-i', i); box.style.setProperty('--pm-n', n);
      st.forEach(function (s, j) {
        s.classList.toggle('is-active', j === i); s.classList.toggle('is-done', j < i); s.classList.toggle('is-todo', j > i);
        if (j === i) s.setAttribute('aria-current', 'step'); else s.removeAttribute('aria-current');
      });
      ev(box, 'pm:step', { index: i });
    }
    function restart() {
      box.classList.remove('pm-playing'); void box.offsetWidth;
      if (auto) { box.style.setProperty('--pm-ms', num(st[i].getAttribute('data-pm-ms'), dwell) + 'ms'); box.classList.add('pm-playing'); }
    }
    function go(k, u) { i = (k + n) % n; paint(); restart(); if (u) ev(box, 'pm:user'); }
    function hold() { box.classList.toggle('pm-held', user || !vis || !!held.h || !!held.f || !!held.t || d.hidden); }
    function setUser(v) { user = v; if (tg) { tg.setAttribute('aria-pressed', String(user)); tg.setAttribute('aria-label', user ? 'Play animation' : 'Pause animation'); } hold(); }
    if (auto && !tg) {
      tg = mk('button', 'pm-pause', '<svg class="pm-i-pause" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M8.5 5.5v13M15.5 5.5v13"/></svg><svg class="pm-i-play" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>');
      tg.type = 'button'; tg.setAttribute('data-pm-toggle', ''); tg.setAttribute('aria-pressed', 'false'); tg.setAttribute('aria-label', 'Pause animation'); box.appendChild(tg);
    }
    box.addEventListener('animationend', function (e) { if (e.animationName === 'pm-tick' && e.target === box) go(i + 1); });
    st.forEach(function (s, j) { s.addEventListener('click', function (e) { if (!e.target.closest('a,button,input,select,textarea,summary')) go(j, 1); }); });
    box.addEventListener('click', function (e) {
      var g = e.target.closest('[data-pm-go]'), t = e.target.closest('[data-pm-toggle]');
      if (g && box.contains(g)) { var v = g.getAttribute('data-pm-go'); go(v === 'next' ? i + 1 : v === 'prev' ? i - 1 : num(v, 0), 1); }
      if (t && box.contains(t)) setUser(!user);
    });
    box.addEventListener('keydown', function (e) {
      var t = e.target; if (!(t.matches && t.matches('[data-pm-go],[data-pm-step]'))) return;
      var k = e.key;
      if (k === 'ArrowRight' || k === 'ArrowDown') { e.preventDefault(); go(i + 1, 1); } else if (k === 'ArrowLeft' || k === 'ArrowUp') { e.preventDefault(); go(i - 1, 1); }
      else if (k === 'Home') { e.preventDefault(); go(0, 1); } else if (k === 'End') { e.preventDefault(); go(n - 1, 1); }
    });
    box.addEventListener('pointerenter', function (e) { if (e.pointerType !== 'touch') { held.h = 1; hold(); } });
    box.addEventListener('pointerleave', function () { held.h = 0; hold(); });
    box.addEventListener('focusin', function () { held.f = 1; hold(); });
    box.addEventListener('focusout', function (e) { if (!box.contains(e.relatedTarget)) { held.f = 0; hold(); } });
    box.addEventListener('touchstart', function () { held.t = 1; hold(); }, { passive: true });
    d.addEventListener('touchstart', function (e) { if (held.t && !box.contains(e.target)) { held.t = 0; hold(); } }, { passive: true });
    d.addEventListener('visibilitychange', hold);
    if (IO) new IntersectionObserver(function (es) { vis = es[0].isIntersecting; hold(); }, { threshold: 0.25 }).observe(box);
    i = num(box.getAttribute('data-pm-start'), 0); paint(); restart(); hold();
    box._pm = { go: go, pause: function () { setUser(true); }, play: function () { setUser(false); } };
  }

  /* ---------- reading progress: [data-pm-progress] (whole page) or [data-pm-progress=".article"] (that element) ---------- */
  function progress(bar) {
    var sel = bar.getAttribute('data-pm-progress'), tick = 0; bar.setAttribute('aria-hidden', 'true');
    function upd() {
      tick = 0;
      var el = sel && d.querySelector(sel), r = el && el.getBoundingClientRect(),
        h = el ? r.height - innerHeight : de.scrollHeight - innerHeight, y = el ? -r.top : (window.pageYOffset || de.scrollTop);
      bar.style.setProperty('--pm-progress', Math.max(0, Math.min(1, h > 0 ? y / h : 0)).toFixed(4));
    }
    addEventListener('scroll', function () { if (!tick) tick = requestAnimationFrame(upd); }, { passive: true });
    addEventListener('resize', upd); upd();
  }

  /* ---------- 12-week scrub: [data-pm-scrub] > [data-pm-week]. Injects a range control, sets data-week / --pm-week, fires pm:week ---------- */
  function scrub(box) {
    var wk = $$('[data-pm-week]', box), n = wk.length, w = 1, user = 0, run = 0;
    if (!n) return;
    var ctl = mk('div', 'pm-scrub__ctl'), r = mk('input'), o = mk('output');
    r.type = 'range'; r.min = 1; r.max = n; r.step = 1; r.value = 1; r.setAttribute('aria-label', box.getAttribute('data-pm-label') || 'Week'); o.setAttribute('aria-hidden', 'true');
    ctl.appendChild(r); ctl.appendChild(o); box.insertBefore(ctl, box.firstChild);
    function set(v, u) {
      v = Math.max(1, Math.min(n, v)); w = v; r.value = v; o.textContent = 'Week ' + v; r.setAttribute('aria-valuetext', 'Week ' + v + ' of ' + n);
      box.setAttribute('data-week', v); box.style.setProperty('--pm-week', v);
      wk.forEach(function (e, j) { e.classList.toggle('is-active', j + 1 === v); e.classList.toggle('is-past', j + 1 < v); e.classList.toggle('is-future', j + 1 > v); });
      if (u) user = 1;
      ev(box, 'pm:week', { week: v });
    }
    r.addEventListener('input', function () { set(+r.value, 1); });
    wk.forEach(function (e, j) { e.addEventListener('click', function (x) { if (!x.target.closest('a,button,input')) set(j + 1, 1); }); });
    var s0 = num(box.getAttribute('data-pm-start'), 1); set(s0);
    if (has(box, 'data-pm-autoplay') && !RM && IO) { /* one gentle sweep through every week when first seen, then back to the start */
      new IntersectionObserver(function (es, ob) {
        if (!es[0].isIntersecting) return; ob.disconnect();
        var k = s0, dir = 1;
        (function tickW() { if (user || run) return; k += dir; if (k >= n) dir = -1; set(k); if (!(dir < 0 && k <= s0)) setTimeout(tickW, 420); else run = 1; })();
      }, { threshold: 0.5 }).observe(box);
    }
  }

  /* ---------- mascots: layered WebP atlas per dog; each part rotates about its pivot (CSS in plentum-motion.css) ---------- */
  var MAS = {"corgi":[573,330,573,499,[["base"],["tongue",163,60,35,35,434,333,12.7,55.4],["tail",421,160,149,166,0,333,24.2,18.7],["earTop",7,21,95,89,251,333,78.8,65.6],["earLow",3,133,96,102,152,333,82.1,43.5],["pawA",389,3,44,56,387,333,46.1,84.4],["pawB",447,43,35,57,349,333,55.3,88.9]],2],"spotty":[544,443,544,564,[["base"],["earA",62,33,96,71,158,446,88.4,82.0],["earB",21,99,115,70,257,446,93.3,42.0],["tail",386,259,155,118,0,446,6.8,71.8],["pawH",424,184,31,40,375,446,45.3,86.0]],2],"beagle":[429,418,429,581,[["base"],["earNear",133,68,135,107,113,421,91.8,50.2],["earFar",278,5,113,74,251,421,57.8,97.1],["tail",105,83,110,160,0,421,46.4,90.3],["eyeA",316,121,24,29,367,421,30.6,46.7],["eyeB",374,114,20,26,394,421,54.9,42.0]],2],"golden":[542,490,542,881,[["base"],["tail",474,251,34,143,0,738,-5.9,104.3],["head",88,3,243,213,273,493,50.5,96.3],["wind",0,4,540,484,0,493,50.2,49.2,270,242],["leaf1",7,274,54,35,148,738,44.1,53.0],["leaf2",29,37,60,37,85,738,59.4,54.3],["leaf3",344,76,50,31,205,738,53.7,47.7],["leaf4",452,81,45,42,37,738,56.8,45.3]],2],"flagship":[471,422,471,520,[["base"],["tailS",0,245,47,80,54,425,89.7,73.9],["earS",7,145,82,80,104,425,93.5,39.8],["tailB",409,177,51,95,0,425,36.5,84.8]],2]};
  var mio = IO && new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      var el = e.target; el.classList.toggle('is-off', !e.isIntersecting);
      if (e.isIntersecting && el._atlas) { el.style.setProperty('--atlas', 'url(' + el._atlas + ')'); el._atlas = 0; }
    });
  }, { rootMargin: '300px' });
  var gio = IO && new IntersectionObserver(function (es, ob) {
    es.forEach(function (e) { if (e.isIntersecting) { ob.unobserve(e.target); pose(e.target, 'happy', 1900); } });
  }, { threshold: 0.6 });
  function pose(el, name, ms) {
    el.classList.toggle('is-happy', name === 'happy'); clearTimeout(el._pt);
    if (ms && name === 'happy') el._pt = setTimeout(function () { el.classList.remove('is-happy'); }, ms);
  }
  function mascot(el) {
    var nm = el.getAttribute('data-pm-mascot'), m = MAS[nm];
    if (!m || el.querySelector('.pm-m__box')) return;
    var W = m[0], H = m[1], AW = m[2], AH = m[3], box = mk('span', 'pm-m__box'), rig = mk('span', 'pm-m__rig');
    function pc(a, b) { return (a / b * 100).toFixed(3) + '%'; }
    el.classList.add('pm-m', 'pm-m--' + nm);
    el.style.setProperty('--ar', (W / H).toFixed(4));
    m[4].forEach(function (l) { /* [name, x, y, w, h, atlasX, atlasY, originX%, originY%] in canvas px; base = whole canvas */
      var p = mk('i', 'pm-p'), x = l[1] || 0, y = l[2] || 0, w = l[3] || W, h = l[4] || H, aw = l[9] || w, ah = l[10] || h; /* aw/ah: atlas size when the part is stored smaller */
      p.setAttribute('data-p', l[0]);
      p.style.cssText = 'left:' + pc(x, W) + ';top:' + pc(y, H) + ';width:' + pc(w, W) + ';height:' + pc(h, H) + ';background-size:' + pc(AW, aw) + ' ' + pc(AH, ah) +
        ';background-position:' + (AW > aw ? pc(l[5] || 0, AW - aw) : '0%') + ' ' + (AH > ah ? pc(l[6] || 0, AH - ah) : '0%') + (l.length > 7 ? ';transform-origin:' + l[7] + '% ' + l[8] + '%' : '');
      rig.appendChild(p);
    });
    box.appendChild(rig); el.appendChild(box);
    if (has(el, 'data-pm-label')) { el.setAttribute('role', 'img'); el.setAttribute('aria-label', el.getAttribute('data-pm-label')); } else el.setAttribute('aria-hidden', 'true');
    var url = BASE + 'pm-mascot-' + nm + '-' + m[5] + '.webp';
    if (RM || has(el, 'data-pm-static')) el.classList.add('is-still');
    if (!mio) { el.style.setProperty('--atlas', 'url(' + url + ')'); return; }
    el._atlas = url; el.classList.add('is-off'); mio.observe(el);
    var host = el.closest('[data-pm-host]') || el.parentElement || el;
    if (!RM && !has(el, 'data-pm-static')) {
      host.addEventListener('mouseenter', function () { pose(el, 'happy'); }); host.addEventListener('mouseleave', function () { pose(el, 'idle'); });
      host.addEventListener('focusin', function () { pose(el, 'happy'); }); host.addEventListener('focusout', function () { pose(el, 'idle'); });
      if (el.getAttribute('data-pm-pose') !== 'idle') gio.observe(el);
    }
    if (el.getAttribute('data-pm-pose') === 'happy') pose(el, 'happy');
  }

  /* ---------- logo moment: one sparkle by the header logo on the first home view of a session ---------- */
  function logo() {
    try {
      if (RM || !d.body.classList.contains('template-index') || sessionStorage.getItem('pm-logo')) return;
      var a = d.querySelector('[data-pm-logo]') || d.querySelector('.wt-header__logo__link'); if (!a) return;
      sessionStorage.setItem('pm-logo', '1');
      var s = mk('span', 'pm-spark', '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1.5c.9 6.4 3 8.6 9.5 10.5-6.5 1.9-8.6 4.1-9.5 10.5-.9-6.4-3-8.6-9.5-10.5C9 10.1 11.1 7.9 12 1.5z"/></svg>');
      a.classList.add('pm-logo-play'); a.appendChild(s);
      setTimeout(function () { s.remove(); a.classList.remove('pm-logo-play'); }, 2200);
    } catch (e) {}
  }

  /* inline wordmark (snippet pm-logo): visible when seen, reveals left to right once per session */
  var lio = IO && new IntersectionObserver(function (es, ob) {
    es.forEach(function (e) {
      if (!e.isIntersecting) return; ob.unobserve(e.target); var el = e.target, played = false;
      try { played = sessionStorage.getItem('pm-wm'); if (!played) sessionStorage.setItem('pm-wm', '1'); } catch (x) { played = true; }
      el.classList.add('is-on'); if (!played && !RM) el.classList.add('is-play');
    });
  }, { threshold: 0.4 });
  function wordmark(el) { if (lio && !RM) lio.observe(el); else el.classList.add('is-on'); }

  var MODS = [['[data-pm-reveal],[data-pm-stagger]', reveal], ['.pm-logo', wordmark], ['[data-pm-tabs]', tabs], ['[data-pm-steps]', steps],
    ['[data-pm-progress]', progress], ['[data-pm-scrub]', scrub], ['[data-pm-mascot]', mascot]];
  function init(root) {
    root = root || d;
    MODS.forEach(function (m) {
      find(root, m[0]).forEach(function (el) {
        var k = el._pmk || (el._pmk = {}); if (k[m[1].name]) return; k[m[1].name] = 1;
        try { m[1](el); } catch (e) {}
      });
    });
  }
  de.classList.add('pm-on');
  window.PM = { init: init, pose: pose, reduced: RM, version: '1.0' };
  init(); logo();
  d.addEventListener('shopify:section:load', function (e) { init(e.target); });
  if ('MutationObserver' in window) {
    new MutationObserver(function (ms) {
      ms.forEach(function (m) { [].forEach.call(m.addedNodes, function (n) { if (n.nodeType === 1) init(n); }); });
    }).observe(d.body || de, { childList: true, subtree: true });
  }
})();
